// src/pages/Readiness.js
//
// THE COMMITTEE READINESS SURFACE (ISS-26).
//
// WHY IT EXISTS. This platform's characteristic failure is a missing precondition and a screen that
// reads as though all is well. Three instances surfaced in a single day (2026-08-28): a Local
// Chairperson with no scopes saw "0 claims" rather than "you are not provisioned"; a claim routed to
// a VACANT seat carried routingStatus 'routed' and so escaped the Unrouted filter; and LC1962-342A
// sat in the reassign picklist with zero mapped locations, unreachable by any claim. Every one of
// them was invisible until somebody went looking.
//
// So this page has one job: NAME WHAT IS NOT YET STOCKED, per committee, before it silently decides
// an outcome. It is a mirror of the data the app already ships, not a new source of truth.
//
// WHAT IT MUST NEVER DO. It must never show a reassuring zero for something it cannot see. Where a
// gap class is not computable in the browser it says so in those words and is rendered as UNKNOWN,
// not as clear. An honest UNKNOWN is the entire point; a false zero is the defect this page exists
// to end.
//
// PAIRED WITH scripts/ycp-verify.js RDY-1, which computes the same gaps from the same source files.
// A page nobody opens on a Tuesday is how the 29 August submissions were nearly missed - the
// verifier is the thing Bob actually reads. If this page and that check ever disagree, one of them
// has been edited alone and both are suspect.

import React, { useMemo, useState } from 'react';
import { LC_ROSTER } from '../data/lcRoster';
import useSeatsOfRecord from '../hooks/useSeatsOfRecord';   // WO-001 S3 (section 240): seats from the spine when seeded
import { SCOPE_KEY_LOOKUP } from '../data/scopeKeyData';
import { AGREEMENTS, COMMITTEE_LOOKUP, AGREEMENT_LOOKUP } from '../data/agreementData';
import { CLAIM_REQUIREMENTS } from '../data/claimRequirements';
import { WAGE_RATES } from '../data/wageRates';
import { hasRuleCatalog } from '../data/ruleSourcing';
import RulingRef from '../components/RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer

// A seat "has a holder" only when its roster label carries neither marker. Both are deliberate
// values written by scripts/gen-lc-roster.js, not accidents:
//   VACANT        - nobody holds the office. Ruling 60: the work and the authority vest in the GC.
//   NOT OF RECORD - somebody may hold it, but no sitting General Chairperson has attested who.
//                   Ruling 54 put the ten real names back into a segregated file until launch.
// Ruling 61: this resolves at ONBOARDING, when a real person holds a real account - not by
// attestation now. Until then every one of these rows is expected to be here.
function holderState(label) {
  if (/VACANT/i.test(label)) return 'VACANT';
  if (/NOT OF RECORD/i.test(label)) return 'NOT OF RECORD';
  return 'HELD';
}

function committeeOf(craft, railroadKey) {
  const byCraft = COMMITTEE_LOOKUP[craft];
  return (byCraft && byCraft[railroadKey]) || null;
}

export function computeReadiness(rosterIn) {
  const ROSTER = Array.isArray(rosterIn) && rosterIn.length ? rosterIn : LC_ROSTER;   // S3: the spine's projection when a committee is seeded, the bundle otherwise
  // ---- locations per seat, counted from the routing table the app actually routes on
  const locationsPerSeat = {};
  Object.keys(SCOPE_KEY_LOOKUP).forEach(k => {
    const seat = SCOPE_KEY_LOOKUP[k];
    locationsPerSeat[seat] = (locationsPerSeat[seat] || 0) + 1;
  });

  const seats = ROSTER.map(s => ({
    scopeKey: s.scopeKey,
    craft: s.craft,
    railroadKey: s.railroadKey,
    label: s.label,
    committee: committeeOf(s.craft, s.railroadKey),
    holder: holderState(s.label),
    locations: locationsPerSeat[s.scopeKey] || 0,
  }));

  // A seat in the roster that no location routes to is unreachable: it can be picked in a reassign
  // control and no claim can ever arrive there. That was LC1962-342A before 2026-08-28.
  const unreachableSeats = seats.filter(s => s.locations === 0);

  // A seat with locations but no holder is the dangerous one: claims arrive and nobody is there.
  const unheldWithWork = seats.filter(s => s.holder !== 'HELD' && s.locations > 0);

  // A routing target that is not in the roster at all - a location pointing at a seat that does not
  // exist as a record. Nothing can reassign it, because the picker is built from the roster.
  const rosterKeys = new Set(seats.map(s => s.scopeKey));
  const orphanTargets = Object.keys(locationsPerSeat)
    .filter(k => !rosterKeys.has(k))
    .map(k => ({ scopeKey: k, locations: locationsPerSeat[k] }));

  // ---- agreements: a vetted rule catalog, and a claim-requirements profile
  // hasRuleCatalog() is the gate the claim path itself uses. It is NOT the same question as
  // "is there a signed primary document on disk" - the platform has no field for that, and this
  // page does not invent one. ISS-2 tracks the missing CSX 2002 and D&H 2007 scans.
  const agreementTitles = [];
  Object.keys(AGREEMENT_LOOKUP).forEach(go => {
    const byCraft = AGREEMENT_LOOKUP[go] || {};
    Object.keys(byCraft).forEach(craft => {
      const byRail = byCraft[craft] || {};
      Object.keys(byRail).forEach(rail => {
        agreementTitles.push({ committee: go, craft, railroadKey: rail, title: byRail[rail] });
      });
    });
  });

  const withoutCatalog = agreementTitles.filter(a => !hasRuleCatalog(a.title));
  const withoutRequirements = agreementTitles.filter(a => !CLAIM_REQUIREMENTS[a.title]);

  // ---- units without a sourced rate. "Unit" = the carrier property + craft a claim is valued in.
  // A unit with no SOURCED rate cannot be valued at all; a projected rate is not a fact.
  const sourcedUnits = new Set(
    WAGE_RATES
      .filter(r => String(r.rate_status || '').toLowerCase() === 'sourced')
      .map(r => (r.carrier_property || '') + '|' + (r.craft || ''))
  );
  const unitsSeen = {};
  seats.forEach(s => { unitsSeen[s.railroadKey + '|' + s.craft] = { railroadKey: s.railroadKey, craft: s.craft, committee: s.committee }; });
  const unitsWithoutRates = Object.keys(unitsSeen)
    .filter(k => !sourcedUnits.has(k))
    .map(k => unitsSeen[k]);

  return {
    seats, unreachableSeats, unheldWithWork, orphanTargets,
    agreementTitles, withoutCatalog, withoutRequirements,
    unitsWithoutRates,
    totals: {
      seats: seats.length,
      locations: Object.keys(SCOPE_KEY_LOOKUP).length,
      agreements: agreementTitles.length,
      platformAgreements: AGREEMENTS.length,
    },
  };
}

const C = {
  bg: '#060E1A', panel: '#0B1626', line: '#2E4A6B',
  dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2',
};

function Section({ title, count, unit, children, tone }) {
  const colour = tone === 'unknown' ? C.dim : (count > 0 ? C.bad : C.ok);
  return (
    <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    padding: '10px 14px', borderBottom: '1px solid ' + C.line }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, letterSpacing: 0.2 }}>{title}</div>
        <div style={{ fontSize: 12, fontWeight: 700, color: colour }}>
          {tone === 'unknown' ? 'UNKNOWN' : count + ' ' + unit}
        </div>
      </div>
      <div style={{ padding: '10px 14px', fontSize: 12, color: C.dim, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function Row({ left, right, note }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12,
                  padding: '5px 0', borderBottom: '1px dotted ' + C.line }}>
      <div style={{ color: C.text }}>{left}{note ? <div style={{ color: C.dim, fontSize: 11 }}>{note}</div> : null}</div>
      <div style={{ color: C.accent, whiteSpace: 'nowrap' }}>{right}</div>
    </div>
  );
}

export default function Readiness({ user }) {
  const spine = useSeatsOfRecord(user && user.committee);
  const r = useMemo(() => computeReadiness(spine.source === 'spine' ? spine.roster : null), [spine.roster, spine.source]);
  const committees = useMemo(() => {
    const set = new Set(r.seats.map(s => s.committee).filter(Boolean));
    return Array.from(set).sort();
  }, [r]);
  const [committee, setCommittee] = useState(committees[0] || null);

  const seats = r.seats.filter(s => !committee || s.committee === committee);
  const unheld = r.unheldWithWork.filter(s => !committee || s.committee === committee);
  const unreachable = r.unreachableSeats.filter(s => !committee || s.committee === committee);
  const noCatalog = r.withoutCatalog.filter(a => !committee || a.committee === committee);
  const noReqs = r.withoutRequirements.filter(a => !committee || a.committee === committee);
  const noRates = r.unitsWithoutRates.filter(u => !committee || u.committee === committee);
  const claimsAtUnheld = unheld.reduce((n, s) => n + s.locations, 0);

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '0 20px 28px', background: C.bg }}>
      <div style={{ padding: '18px 0 10px' }}>
        <h1 style={{ margin: 0, color: C.text, fontSize: 20 }}>Committee readiness</h1>
        <p style={{ margin: '6px 0 0', color: C.dim, fontSize: 12, lineHeight: 1.6, maxWidth: 720 }}>
          What is not yet stocked. Every figure here is counted from the data this application
          actually runs on, at the moment you opened the page. A gap this page cannot see is
          labelled <b style={{ color: C.text }}>UNKNOWN</b> and never as clear.
        </p>
      </div>

      {/* A11 (audit 2026-09-22). useSeatsOfRecord already returns `error`, and this page ignored it -
          so a refused seats read produced an EMPTY roster, and every count below was computed from
          nothing and printed as fact. "Every seat is held" when the read was refused is the exact
          inversion of what this page promises three lines above: a gap it cannot see is labelled
          UNKNOWN and never as clear. */}
      {spine.error ? (
        <div style={{ border: '1px solid ' + C.accent, background: '#2D1A00', color: C.accent,
                      padding: '12px 14px', margin: '4px 0 16px', fontSize: 13, lineHeight: 1.6 }}>
          &#9888; <b>The seats of record could not be read</b> ({String(spine.error)}).
          <div style={{ color: C.dim, fontSize: 12, marginTop: 4 }}>
            Every figure on this page is counted from that read. It is <b>UNKNOWN</b>, not clear &mdash;
            nothing below is shown, because a zero here would read as "nothing is missing".
          </div>
        </div>
      ) : (
      <>
      {committees.length > 1 && (
        <div style={{ display: 'flex', gap: 8, margin: '4px 0 16px', flexWrap: 'wrap' }}>
          {committees.map(g => (
            <button key={g} onClick={() => setCommittee(g)}
              style={{ padding: '5px 12px', fontSize: 12, cursor: 'pointer', background: 'transparent',
                       border: '1px solid ' + (committee === g ? C.accent : C.line),
                       color: committee === g ? C.accent : C.dim }}>{g}</button>
          ))}
          <button onClick={() => setCommittee(null)}
            style={{ padding: '5px 12px', fontSize: 12, cursor: 'pointer', background: 'transparent',
                     border: '1px solid ' + (committee === null ? C.accent : C.line),
                     color: committee === null ? C.accent : C.dim }}>All</button>
        </div>
      )}

      <Section title="Seats with work and no holder" count={unheld.length} unit="seat(s)">
        {unheld.length === 0
          ? 'Every seat that claims route to is held.'
          : <>
              <div style={{ marginBottom: 6 }}>
                <b style={{ color: C.text }}>{claimsAtUnheld}</b> mapped location(s) route to a seat nobody holds.
                The work and the authority vest in the General Chairperson until the seat is
                filled or the claim is reassigned - so this is survivable, but it is never invisible.<RulingRef user={user} n={60} why="When a Local Chairperson's seat is vacant, the work and authority over its claims and cases belong to the General Chairperson." />
              </div>
              {unheld.map(s => (
                <Row key={s.scopeKey} left={s.scopeKey} note={s.craft + ' | ' + s.railroadKey}
                     right={s.holder + '  -  ' + s.locations + ' location(s)'} />
              ))}
            </>}
      </Section>

      <Section title="Seats no claim can reach" count={unreachable.length} unit="seat(s)">
        {unreachable.length === 0
          ? 'Every seat in the roster has at least one mapped location.'
          : <>
              <div style={{ marginBottom: 6 }}>
                In the roster and in the reassign picker with <b style={{ color: C.text }}>zero</b> mapped
                locations. A claim cannot arrive, and choosing one in a picker sends work nowhere.
              </div>
              {unreachable.map(s => <Row key={s.scopeKey} left={s.scopeKey} note={s.craft + ' | ' + s.railroadKey} right="0 locations" />)}
            </>}
      </Section>

      {r.orphanTargets.length > 0 && (
        <Section title="Locations routing to a seat that is not in the roster" count={r.orphanTargets.length} unit="target(s)">
          <div style={{ marginBottom: 6 }}>
            The routing table sends claims to a designation the roster does not contain, so the reassign
            picker - which is built from the roster - cannot offer it.
          </div>
          {r.orphanTargets.map(o => <Row key={o.scopeKey} left={o.scopeKey} right={o.locations + ' location(s)'} />)}
        </Section>
      )}

      <Section title="Agreements with no vetted rule catalog" count={noCatalog.length} unit="agreement(s)">
        {noCatalog.length === 0
          ? 'Every agreement offered to a claim has a rule catalog behind it.'
          : <>
              <div style={{ marginBottom: 6 }}>
                A claim filed under these gets no sourced rule text. Any language an analysis returns for
                them is suppressed rather than shown, which is correct - and it means the member picks a
                basis with nothing quotable underneath it.
              </div>
              {noCatalog.map(a => <Row key={a.committee + a.title} left={a.title} note={a.craft + ' | ' + a.railroadKey} right="no catalog" />)}
            </>}
      </Section>

      <Section title="Agreements with no filing-window profile" count={noReqs.length} unit="agreement(s)">
        {noReqs.length === 0
          ? 'Every agreement carries its claims rule, filing window and requirement list.'
          : <>
              <div style={{ marginBottom: 6 }}>
                No entry in the claim requirements spine, so <b style={{ color: C.text }}>no filing deadline
                is computed and none is shown</b>. The member is told nothing about the clock he is on.
              </div>
              {noReqs.map(a => <Row key={a.committee + a.title} left={a.title} note={a.craft + ' | ' + a.railroadKey} right="no window" />)}
            </>}
      </Section>

      <Section title="Units with no sourced pay rate" count={noRates.length} unit="unit(s)">
        {noRates.length === 0
          ? 'Every carrier and craft that claims route to has at least one sourced rate.'
          : <>
              <div style={{ marginBottom: 6 }}>
                A claim here cannot be valued. A projected or chained rate is not a sourced one and does
                not count toward this line.
              </div>
              {noRates.map(u => <Row key={u.railroadKey + u.craft} left={u.craft} note={u.railroadKey} right="no sourced rate" />)}
            </>}
      </Section>

      <Section title="Accounts provisioned without a scope" count={0} unit="" tone="unknown">
        <b style={{ color: C.text }}>This page cannot see it, and says so rather than showing a zero.</b>{' '}
        Role claims and scopes live on the authentication token and in
        <span style={{ color: C.text }}> data\YCP_USER_ROLES.csv</span>; a browser holds only the signed-in
        user's own claims, so no client can enumerate the accounts of others. An unprovisioned officer sees
        an empty list rather than an explanation - the failure that surfaced on 2026-08-28 - and the only
        thing that can count them is the verifier, from the roles file, or the onboarding flow
        the record calls for.<RulingRef user={user} n={65} why="Officers come onto the platform through onboarding, and a Local Chairperson can complete it without the General Chairperson." /> Signed in now as <span style={{ color: C.accent }}>{(user && (user.roleDisplay || user.role)) || 'unknown role'}</span>
        {user && Array.isArray(user.scopes) ? ' with ' + user.scopes.length + ' scope(s).' : '.'}
      </Section>

      <div style={{ color: C.dim, fontSize: 11, lineHeight: 1.6, borderTop: '1px solid ' + C.line, paddingTop: 10 }}>
        Counted from {r.totals.seats} roster seat(s), {r.totals.locations} mapped location(s) and{' '}
        {r.totals.agreements} agreement title(s) shipped in this build. Sources:
        <span style={{ color: C.text }}> src\data\lcRoster.js</span>,
        <span style={{ color: C.text }}> src\data\scopeKeyData.js</span>,
        <span style={{ color: C.text }}> src\data\agreementData.js</span>,
        <span style={{ color: C.text }}> src\data\claimRequirements.js</span>,
        <span style={{ color: C.text }}> src\data\wageRates.js</span> and
        <span style={{ color: C.text }}> src\data\ruleSourcing.js</span>. The same gaps are computed
        independently by RDY-1 in <span style={{ color: C.text }}>scripts\ycp-verify.js</span>; if the two
        ever disagree, one has been edited alone and neither should be trusted until they are reconciled.
      </div>
      </>
      )}
    </div>
  );
}
